import { formatBytes } from '../api/storage';

/**
 * Modal shown when an upload/publish fails with HTTP 413 and
 * error === 'storage_quota_exceeded'. Reads the server's detail payload:
 *   { bytes_used, quota_bytes, incoming_size }  (all bigint-as-string)
 *
 * Contact email defaults to admin@iotechnam.com; set
 * window.__platformAdminContact at boot time if you want to override.
 */
export interface QuotaExceededDetail {
  bytes_used?: string;
  quota_bytes?: string;
  incoming_size?: string;
}

interface Props {
  detail: QuotaExceededDetail;
  onClose: () => void;
}

export function QuotaExceededModal({ detail, onClose }: Props) {
  const adminContact =
    (window as unknown as { __platformAdminContact?: string }).__platformAdminContact
    ?? 'admin@iotechnam.com';

  const used = detail.bytes_used ? formatBytes(detail.bytes_used) : 'unknown';
  const quota = detail.quota_bytes ? formatBytes(detail.quota_bytes) : 'unknown';
  const incoming = detail.incoming_size ? formatBytes(detail.incoming_size) : null;

  // How much would this upload overshoot the quota?
  let overshoot: string | null = null;
  if (detail.bytes_used && detail.quota_bytes && detail.incoming_size) {
    try {
      const overBy = BigInt(detail.bytes_used) + BigInt(detail.incoming_size) - BigInt(detail.quota_bytes);
      if (overBy > 0n) overshoot = formatBytes(overBy);
    } catch { /* ignore */ }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7em', marginBottom: '0.6em' }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.15)',
            color: 'var(--danger)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.4em', fontWeight: 700, fontFamily: 'monospace',
          }}>!</div>
          <h2 style={{ margin: 0, color: 'var(--danger)' }}>Storage quota exceeded</h2>
        </div>

        <p style={{ color: 'var(--fg-1)', lineHeight: 1.55, marginTop: '0.6em' }}>
          Your organization has used <strong style={{ fontFamily: 'monospace' }}>{used}</strong> of
          its <strong style={{ fontFamily: 'monospace' }}>{quota}</strong> storage quota.
          {incoming && (
            <> Uploading this <strong style={{ fontFamily: 'monospace' }}>{incoming}</strong> file
              {overshoot && <> would exceed the limit by <strong style={{ fontFamily: 'monospace', color: 'var(--warn)' }}>{overshoot}</strong></>}.
            </>
          )}
        </p>

        <p style={{ color: 'var(--fg-2)', lineHeight: 1.55 }}>
          To upload more files, please contact your platform administrator at{' '}
          <a href={`mailto:${adminContact}`} style={{ fontFamily: 'monospace' }}>{adminContact}</a>{' '}
          to request an increase.
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.2em' }}>
          <button className="primary" onClick={onClose}>Understood</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper: given a caught error from a fetch/api call, return the quota
 * detail if it was a 413 storage_quota_exceeded, else null.
 */
export function extractQuotaDetail(err: unknown): QuotaExceededDetail | null {
  if (!err || typeof err !== 'object') return null;
  const asAny = err as { status?: number; detail?: { error?: string; detail?: QuotaExceededDetail } };
  if (asAny.status !== 413) return null;
  if (asAny.detail?.error !== 'storage_quota_exceeded') return null;
  return asAny.detail.detail ?? {};
}
